// covered-by: frontends/carrie/src/routes/ask-puntt/components/try-walkthrough/__tests__/TryWalkthroughDialog.test.tsx
import { CircleNotch, UploadSimple } from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { type AttachmentItem } from "../../hooks/useAttachedFiles";
import { ACCEPTED_FILE_TYPES, useFileDrop } from "../../hooks/useFileDrop";

type TryWalkthroughUploadProps = {
  onFilesSelected: (files: File[]) => void;
  attachedItems: AttachmentItem[];
  onRemoveFile: (index: number) => void;
};

/**
 * Last-step drop target for the Try walkthrough. Files go through the same
 * `addFiles` pipeline as the home composer so Get started can launch with them.
 */
export function TryWalkthroughUpload({
  onFilesSelected,
  attachedItems,
  onRemoveFile,
}: TryWalkthroughUploadProps) {
  const { t } = useTranslation("ui");
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDragOver, dropZoneProps } = useFileDrop({
    onDrop: onFilesSelected,
  });

  const openPicker = () => inputRef.current?.click();

  return (
    <div className="mt-4">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES.join(",")}
        className="sr-only"
        data-testid="ask-puntt-try-walkthrough-upload-input"
        aria-label={t("tryWalkthrough.uploadInputAriaLabel")}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) onFilesSelected(files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        data-testid="ask-puntt-try-walkthrough-upload"
        aria-label={t("tryWalkthrough.uploadAriaLabel")}
        onClick={openPicker}
        {...dropZoneProps}
        className={`flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
          isDragOver
            ? "border-puntt-accent-9 bg-puntt-accent-3 text-puntt-accent-11"
            : "border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 text-puntt-neutral-gray-11 hover:border-puntt-accent-7"
        }`}
      >
        <UploadSimple size={28} aria-hidden="true" />
        <Text as="span" size="2" weight="medium">
          {t("tryWalkthrough.uploadHint")}
        </Text>
      </button>

      {attachedItems.length > 0 && (
        <Flex gap="2" wrap="wrap" className="mt-3">
          {attachedItems.map((item, idx) => (
            <Flex
              key={item.id}
              align="center"
              gap="1"
              className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
              data-testid={`ask-puntt-try-walkthrough-file-${item.id}`}
            >
              {item.status === "pending" && (
                <CircleNotch
                  size={12}
                  weight="bold"
                  className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                />
              )}
              <Text
                size="1"
                className="max-w-[150px] truncate text-puntt-neutral-gray-11"
              >
                {item.file.name}
                {item.status === "error" && " (failed)"}
              </Text>
              <button
                type="button"
                data-testid={`ask-puntt-try-walkthrough-file-remove-${item.id}`}
                onClick={() => onRemoveFile(idx)}
                className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                aria-label={t("home.removeFileAriaLabel", {
                  name: item.file.name,
                })}
              >
                ×
              </button>
            </Flex>
          ))}
        </Flex>
      )}
    </div>
  );
}
